export interface VisibleRange {
  start: number;
  end: number;
}

export type ViewportReconcileAction =
  | "none"
  | "shiftUp"
  | "shiftDown"
  | "loadMore";

export function splitIntoChunks<T>(items: T[], chunkSize: number): T[][] {
  if (items.length === 0) {
    return [];
  }

  const result: T[][] = [];

  for (let index = 0; index < items.length; index += chunkSize) {
    result.push(items.slice(index, index + chunkSize));
  }

  return result;
}

export function sumChunkHeights(
  from: number,
  to: number,
  chunkHeights: ReadonlyMap<number, number>,
  fallbackHeight: number,
): number {
  if (to <= from) {
    return 0;
  }

  let total = 0;

  for (let index = from; index < to; index += 1) {
    total += chunkHeights.get(index) ?? fallbackHeight;
  }

  return Math.max(0, total);
}

export function resolveMeasuredChunkHeight(input: {
  chunkHeights: ReadonlyMap<number, number>;
  estimatedChunkHeight: number;
}): number {
  const values = Array.from(input.chunkHeights.values()).filter(
    (value) => Number.isFinite(value) && value > 0,
  );

  if (values.length === 0) {
    return Math.max(1, input.estimatedChunkHeight);
  }

  const total = values.reduce((sum, value) => sum + value, 0);
  return Math.max(1, Math.round(total / values.length));
}

export function resolveVisibleRange(input: {
  chunksCount: number;
  windowChunks: number;
  visibleStart: number;
  visibleEnd: number;
}): VisibleRange {
  if (input.chunksCount <= 0) {
    return {
      start: 0,
      end: -1,
    };
  }

  const visibleCount = Math.min(input.windowChunks, input.chunksCount);

  let start = Math.min(input.visibleStart, input.chunksCount - 1);
  let end = Math.min(input.visibleEnd, input.chunksCount - 1);

  if (end < start) {
    end = start;
  }

  const currentVisible = end - start + 1;

  if (currentVisible < visibleCount) {
    end = Math.min(input.chunksCount - 1, start + visibleCount - 1);
    start = Math.max(0, end - visibleCount + 1);
  }

  return { start, end };
}

export function shiftVisibleRangeDown(
  range: VisibleRange,
  chunksCount: number,
): VisibleRange {
  if (range.end >= chunksCount - 1) {
    return range;
  }

  return {
    start: range.start + 1,
    end: range.end + 1,
  };
}

export function shiftVisibleRangeUp(range: VisibleRange): VisibleRange {
  if (range.start <= 0) {
    return range;
  }

  return {
    start: range.start - 1,
    end: range.end - 1,
  };
}

export function buildVisibleChunkIds(input: {
  chunksCount: number;
  visibleStart: number;
  visibleEnd: number;
}): number[] {
  if (input.chunksCount <= 0 || input.visibleEnd < input.visibleStart) {
    return [];
  }

  const ids: number[] = [];

  for (
    let index = input.visibleStart;
    index <= input.visibleEnd && index < input.chunksCount;
    index += 1
  ) {
    ids.push(index);
  }

  return ids;
}

export function resolveViewportAction(input: {
  viewportTop: number;
  viewportBottom: number;
  paddingPx: number;
  rootTop: number;
  totalHeight: number;
  topSpacerHeight: number;
  visibleChunksHeight: number;
  visibleEnd: number;
  chunksCount: number;
}): ViewportReconcileAction {
  const rootBottom = input.rootTop + input.totalHeight;

  if (
    input.viewportBottom + input.paddingPx < input.rootTop ||
    input.viewportTop - input.paddingPx > rootBottom
  ) {
    return "none";
  }

  const visibleTop = input.rootTop + input.topSpacerHeight;
  const visibleBottom = visibleTop + input.visibleChunksHeight;
  const nearBottom = input.viewportBottom + input.paddingPx >= visibleBottom;
  const nearTop = input.viewportTop <= visibleTop + input.paddingPx;

  if (nearBottom) {
    if (input.visibleEnd >= input.chunksCount - 1) {
      return "loadMore";
    }

    return "shiftDown";
  }

  if (nearTop) {
    return "shiftUp";
  }

  return "none";
}

export function shouldLoadMoreForShortDocument(input: {
  viewportHeight: number;
  documentHeight: number;
  rootTop: number;
  rootBottom: number;
  paddingPx: number;
}): boolean {
  const isNearViewport =
    input.rootBottom >= -input.paddingPx &&
    input.rootTop <= input.viewportHeight + input.paddingPx;

  return (
    isNearViewport &&
    input.documentHeight <= input.viewportHeight + input.paddingPx
  );
}

export function canTriggerLoadMore(input: {
  hasOnLoadMore: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  lastLoadRequestAt: number;
  now: number;
  minLoadIntervalMs: number;
}): boolean {
  if (!input.hasOnLoadMore || !input.hasMore || input.isLoadingMore) {
    return false;
  }

  return input.now - input.lastLoadRequestAt >= input.minLoadIntervalMs;
}
